import { motion } from "framer-motion";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { Badge } from "./ui/badge";
import { DollarSign, Users, Calendar, TrendingUp, Gift, RefreshCw, Loader2 } from "lucide-react";
import { useFundingStats } from "@/hooks/useFundingStats";

export const FundingProgress = () => {
  const { data: stats, isLoading, refetch, isRefetching } = useFundingStats();

  const fundingGoal = stats?.goalAmount || 250000;
  const currentAmount = stats?.totalRaised || 0;
  const progressPercentage = stats?.percentFunded || 0;
  const backers = stats?.backerCount || 0;
  
  // Calculate days left (campaign ends in 60 days from launch)
  const campaignEndDate = new Date("2025-02-06"); // 60 days from now
  const today = new Date();
  const daysLeft = Math.max(0, Math.ceil((campaignEndDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));

  const scrollToDonate = () => {
    document.getElementById("donate")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="fund-project" className="py-20 px-4 bg-gradient-to-br from-background to-muted/50">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <Badge className="bg-story-hope/10 text-story-hope border-story-hope/20 mb-4">
            <TrendingUp className="w-4 h-4 mr-2" />
            LIVE FUNDING TRACKER
          </Badge>
          <h2 className="font-serif text-4xl md:text-5xl text-foreground mb-6">
            Help Bring This Story to Life
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Every contribution brings us closer to sharing this powerful story of hope, resilience, and human connection with the world.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Funding Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-2"
          >
            <Card className="h-full border-2 border-story-hope/20 shadow-xl">
              <CardContent className="p-8">
                <div className="space-y-8">
                  {/* Funding Amount */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-baseline gap-2">
                        {isLoading ? (
                          <div className="flex items-center gap-2">
                            <Loader2 className="w-6 h-6 animate-spin text-story-hope" />
                            <span className="text-xl text-muted-foreground">Loading...</span>
                          </div>
                        ) : (
                          <>
                            <span className="text-5xl font-bold text-story-hope">
                              ${currentAmount.toLocaleString()}
                            </span>
                            <span className="text-xl text-muted-foreground">
                              raised of ${fundingGoal.toLocaleString()} goal
                            </span>
                          </>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => refetch()}
                        disabled={isRefetching}
                        className="text-muted-foreground hover:text-story-hope"
                      >
                        <RefreshCw className={`w-4 h-4 ${isRefetching ? "animate-spin" : ""}`} />
                      </Button>
                    </div>
                    <Progress value={progressPercentage} className="h-3 mb-4" />
                    <div className="text-2xl font-semibold text-foreground">
                      {Math.round(progressPercentage)}% funded
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-6">
                    <div className="text-center p-4 bg-muted/50 rounded-lg">
                      <div className="flex items-center justify-center mb-2">
                        <Users className="w-6 h-6 text-story-peace" />
                      </div>
                      <div className="text-3xl font-bold text-foreground">{backers}</div>
                      <div className="text-sm text-muted-foreground">Backers</div>
                    </div>
                    <div className="text-center p-4 bg-muted/50 rounded-lg">
                      <div className="flex items-center justify-center mb-2">
                        <Calendar className="w-6 h-6 text-story-sudan" />
                      </div>
                      <div className="text-3xl font-bold text-foreground">{daysLeft}</div>
                      <div className="text-sm text-muted-foreground">Days to go</div>
                    </div>
                  </div>

                  {/* CTA Button */}
                  <Button
                    size="lg"
                    onClick={scrollToDonate}
                    className="w-full bg-story-hope hover:bg-story-hope/90 text-black font-bold text-xl py-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]"
                  >
                    <DollarSign className="w-6 h-6 mr-2" />
                    Back This Project
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Recent Donations / Rewards Preview */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Card className="h-full border border-muted shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-6">
                  <Gift className="w-5 h-5 text-story-peace" />
                  <h3 className="text-xl font-semibold">Recent Backers</h3>
                </div>

                <div className="space-y-4">
                  {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-story-hope" />
                    </div>
                  ) : stats?.recentDonations && stats.recentDonations.length > 0 ? (
                    stats.recentDonations.map((donation, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="p-4 border border-muted rounded-lg hover:border-story-hope/50 transition-colors"
                      >
                        <div className="flex justify-between items-start mb-1">
                          <span className="font-semibold text-story-hope">${donation.amount}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(donation.date).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-sm text-foreground">{donation.name}</p>
                      </motion.div>
                    ))
                  ) : (
                    <>
                      {/* Placeholder when no donations yet */}
                      <div className="p-4 border border-dashed border-muted rounded-lg text-center">
                        <p className="text-sm text-muted-foreground">
                          Be the first to back this project!
                        </p>
                      </div>
                      <div className="p-4 border border-muted rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-semibold text-story-hope">$20</span>
                          <Badge variant="secondary" className="text-xs">POPULAR</Badge>
                        </div>
                        <h4 className="font-medium mb-1">Supporter Tier</h4>
                        <p className="text-sm text-muted-foreground">
                          Credit + shareholder status
                        </p>
                      </div>
                      <div className="p-4 border border-muted rounded-lg">
                        <div className="mb-2">
                          <span className="font-semibold text-story-hope">$5,000</span>
                        </div>
                        <h4 className="font-medium mb-1">Executive Producer</h4>
                        <p className="text-sm text-muted-foreground">
                          VIP credit + podcast interview
                        </p>
                      </div>
                    </>
                  )}
                </div>

                <Button 
                  variant="outline" 
                  onClick={scrollToDonate}
                  className="w-full mt-6 border-story-peace text-story-peace hover:bg-story-peace hover:text-background"
                >
                  View All Tiers
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
